import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { Anchor, ColEven, HorizontalLine, InputAndButtonGrid, OrderWrapper } from '@components/ringil3/styles.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { findValidationMessage, findValidationMessageRaw } from '@utils/validation.tsx';
import RingilTextArea from '@components/framework/input/RingilTextArea';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgPlusCircle from '@icons/plus-circle.svg?react';
import InputSKU from '@components/ringil3/Input/InputSKU/InputSKU.tsx';
import InputTextWithSuffix from '@components/ringil3/Input/InputTextWithSuffix/InputTextWithSuffix.tsx';
import { formatWeight, isNotBlank, isNotNullOrUndefined, parseNumberOrUndefined } from '@utils/utils.tsx';
import InputStackable from '@components/ringil3/Input/InputStackable/InputStackable.tsx';
import InputNoteWithLink from '@components/ringil3/Input/InputNoteWithLink/InputNoteWithLink.tsx';
import { calculateOrderVolume, emptyOrder, Order } from '@components/framework/orders/common.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { fullRequiredConfig, OrdersConfig } from '@components/framework/orders/validation.ts';
import VisibilityComponent from '@components/framework/orders/VisibitiyComponent.tsx';
import SvgIconPlus from '@components/framework/orders/IconPlus.tsx';
import LinkWithIcon from '@components/ringil3/Links/LinkWithIcon.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';

import { CargoTemplateWithoutIdDTO, ExternalOrderRequest, ExternalOrderResponse, FeaturesEnum } from '@api/ringil-api.v2.tsx';
import SvgLoadDown from '@icons/LoadDown.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import produce from 'immer';
import { ValidationError } from 'yup';
import { InputNumber } from '@components/framework/input/InputNumber';
import FoldableEanCodes from '@components/ringil3/FoldableEanCodes/FoldableEanCodes.tsx';
import InputPriceWithCurrency from '@components/ringil3/Input/InputPriceWithCurrency/InputPriceWithCurrency.tsx';
import ExternalOrdersDropdown, { buildExternalOrderId } from '@components/RingilComponents/ExternalOrdersDropdown.tsx';
import SecuredArea from '@components/security/SecuredArea';
import { SecuredConfig } from '@components/security/securedDecoration';

interface ComponentProps {
  config?: OrdersConfig;
  canBeEmpty?: boolean;
  generateOrderId?: boolean;
  allUnloadingLocations?: { id: string; name: string }[];
  onChange?: (orders: Order[]) => void;
  orders: Order[];
  validationErrors: ValidationError[];
  isReservationOrderLinkedToExpOrder?: boolean;
  companyId?: string;
  dataTestIdPrefix?: string;
  buildOrderTestId?: (orderIndex: number, suffix: string) => string | undefined;
  buildManUnitTestId?: (orderIndex: number, itemIndex: number, suffix?: string) => string | undefined;
  externalOrders?: ExternalOrderResponse[];
  externalIdPresent?: boolean;
  // Whether the external orders dropdown shows the NO_ORDERS_NOW option. Defaults to true.
  showNoOrdersNow?: boolean;
  // Opt-in (SN create/edit): grey-out + block external orders whose span doesn't cover the current date.
  disableOrdersOutsideCurrentDate?: boolean;
  disabled?: boolean;
  // Opt-in (SN create): wrap policy-driven order fields in the security overlay. Omitted elsewhere → no overlay.
  fieldSecured?: (key: 'orderAmount' | 'orderValue' | 'itemNote', orderIndex: number, itemIndex?: number) => SecuredConfig | undefined;
  // Opt-in: controls rendered next to each order's headline (create shipment uses it to move an
  // order's loading stop). Callers that omit it get the plain headline.
  orderHeaderActions?: (order: Order, orderIndex: number) => React.ReactNode;
  // Opt-in: a full-width block under each order's headline, for messages about the order as a whole.
  orderBanner?: (order: Order, orderIndex: number) => React.ReactNode;
  // Opt-in: controls rendered beside the add-order button at the foot of the list.
  footerActions?: React.ReactNode;
  // What one entry is called here. Reservations call it "Order/Reference"; everywhere else it is
  // the goods being picked up, so the default reads "Load order".
  orderTitle?: string;
}

const OrdersComponent: React.FC<ComponentProps> = ({
  config = fullRequiredConfig(true),
  canBeEmpty = false,
  generateOrderId = false,
  orders,
  onChange,
  validationErrors,
  allUnloadingLocations,
  isReservationOrderLinkedToExpOrder = false,
  companyId,
  dataTestIdPrefix,
  buildOrderTestId,
  buildManUnitTestId,
  externalOrders,
  externalIdPresent = false,
  showNoOrdersNow = true,
  disableOrdersOutsideCurrentDate = false,
  disabled = false,
  fieldSecured,
  orderHeaderActions,
  orderBanner,
  orderTitle,
  footerActions,
}) => {
  const { tr } = useTranslationLgs();
  const authUser = useContext(AuthenticatedUserContext);
  const volumeOfOrderEnabled = authUser?.hasFeature([FeaturesEnum.VOLUME_OF_ORDER]) ?? false;
  // Read-only when the caller disables editing (e.g. SN without ROLE_SN_RW) or the order is reservation-linked.
  const isReadOnly = disabled || isReservationOrderLinkedToExpOrder;
  const wrapSecured = (cfg: SecuredConfig | undefined, node: React.ReactNode): React.ReactNode => (cfg ? <SecuredArea {...cfg}>{node}</SecuredArea> : node);
  const [isCargoDetailsOpen, setIsCargoDetailsOpen] = useState<boolean>(false);

  const orderNameLengthLimit = 1500;

  // Set default unloading location if there's only one option
  useEffect(() => {
    if (allUnloadingLocations?.length === 1 && orders.length > 0) {
      const defaultLocation = allUnloadingLocations[0];
      onChange(
        produce(orders, draft => {
          draft.forEach(order => {
            if (!order.unloadingLocationId) {
              order.unloadingLocationId = defaultLocation.id;
            }
          });
        }),
      );
    }
  }, [allUnloadingLocations, orders]);

  const addOrder = () => {
    const order = emptyOrder(config.cargo.required, generateOrderId);
    onChange([...orders, order]);
  };

  const deleteOrder = (orderIndex: number) => {
    onChange(orders.filter((_, i) => i !== orderIndex));
  };

  const addOrderItem = (orderIndex: number) => {
    onChange(
      produce(orders, draft => {
        draft[orderIndex].items = [
          ...draft[orderIndex].items,
          {
            itemNote: null,
            amount: null,
            cargoTemplateCode: null,
            stackable: false,
            decimalPlaces: 0,
          },
        ];
      }),
    );
  };

  const deleteOrderItem = (orderIndex: number, itemIndex: number) => {
    onChange(
      produce(orders, draft => {
        draft[orderIndex].items = draft[orderIndex].items.filter((_, i) => i !== itemIndex);
      }),
    );
  };

  const processChangeCargo = (orderIndex: number, orderItemIndex: number, v: CargoTemplateWithoutIdDTO) => {
    if (v) {
      onChange(
        produce(orders, draft => {
          draft[orderIndex].items[orderItemIndex].cargoTemplateCode = v?.code;
          draft[orderIndex].items[orderItemIndex].decimalPlaces = v?.decimalPlaces;
          draft[orderIndex].items[orderItemIndex].width = v.width;
          draft[orderIndex].items[orderItemIndex].height = v.height;
          draft[orderIndex].items[orderItemIndex].length = v.length;
          draft[orderIndex].items[orderItemIndex].weight = v.weight;
        }),
      );
    }
  };

  // Generate test ID helpers based on dataTestIdPrefix
  const getOrderTestId = (orderIndex: number, suffix: string) => {
    if (buildOrderTestId) return buildOrderTestId(orderIndex, suffix);
    if (!dataTestIdPrefix) return undefined;
    return `${dataTestIdPrefix}-orderReference${orderIndex + 1}-${suffix}`;
  };

  const getManUnitBaseTestId = (orderIndex: number, itemIndex: number) => {
    if (buildManUnitTestId) return buildManUnitTestId(orderIndex, itemIndex);
    if (!dataTestIdPrefix) return undefined;
    return `${dataTestIdPrefix}-orderReference${orderIndex + 1}-manUnit${itemIndex + 1}`;
  };

  const getManUnitTestId = (orderIndex: number, itemIndex: number, suffix: string) => {
    if (buildManUnitTestId) return buildManUnitTestId(orderIndex, itemIndex, suffix);
    if (!dataTestIdPrefix) return undefined;
    return `${dataTestIdPrefix}-orderReference${orderIndex + 1}-manUnit${itemIndex + 1}-${suffix}`;
  };

  return (
    <ColEven>
      {orders?.map((order, orderIndex) => {
        const canDeleteOrder = !isReadOnly && (orders.length > 1 || canBeEmpty == true);

        return (
          <OrderWrapper key={orderIndex} id={`anchor_order_${orderIndex}`}>
            <Anchor id={`order_${orderIndex}`} className={'ordersTabsContent'} />
            <div className={'flex flex-row items-center justify-between gap-2'}>
              <COHeadline variant={'h4'} title={orderTitle ?? tr('OrdersComponent.loadOrder', 'Load order')}></COHeadline>
              <div className={'flex flex-row items-center gap-2'}>
                {orderHeaderActions?.(order, orderIndex)}
                {canDeleteOrder && (
                  <RingilButton
                    mode={'table'}
                    size={'small'}
                    icon={<SvgDelete />}
                    tooltip={tr('OrdersComponent.deleteOrder', 'Delete order')}
                    onClick={() => deleteOrder(orderIndex)}
                  />
                )}
              </div>
            </div>
            {orderBanner?.(order, orderIndex)}
            {externalIdPresent ? (
              <Field
                required={config.orderName.required && (order.externalOrders ?? []).length === 0}
                label={tr(`CreateTimeslotSidebar.externalOrders`, `External orders`)}
                errorMessage={findValidationMessage(validationErrors, `orders[${orderIndex}].externalOrders`, tr)}
              >
                <InputAndButtonGrid>
                  <div>
                    <ExternalOrdersDropdown
                      availableOrders={externalOrders ?? []}
                      selectedIds={(() => {
                        const used = new Set<number>();
                        return (order.externalOrders ?? []).map(eo => {
                          const idx = (externalOrders ?? []).findIndex(
                            (e, i) =>
                              !used.has(i) &&
                              e.orderNumber === eo.orderNumber &&
                              e.timeFrom === eo.timeFrom &&
                              e.timeTo === eo.timeTo,
                          );
                          if (idx >= 0) {
                            used.add(idx);
                            return buildExternalOrderId(idx, externalOrders[idx]);
                          }
                          return `__missing__${eo.orderNumber}`;
                        });
                      })()}
                      onSelectionChange={ids => {
                        const next: ExternalOrderRequest[] = [];
                        ids.forEach(id => {
                          const idx = parseInt(id.split('__')[0], 10);
                          const src = (externalOrders ?? [])[idx];
                          if (!src) return;
                          next.push({
                            orderNumber: src.orderNumber,
                            timeFrom: src.timeFrom,
                            timeTo: src.timeTo,
                          });
                        });
                        onChange(
                          produce(orders, draft => {
                            draft[orderIndex].externalOrders = next;
                          }),
                        );
                      }}
                      readonly={isReadOnly}
                      dataTestIdBase={getOrderTestId(orderIndex, 'id')}
                      showNoOrdersNow={showNoOrdersNow}
                      disableOrdersOutsideCurrentDate={disableOrdersOutsideCurrentDate}
                    />
                  </div>
                </InputAndButtonGrid>
              </Field>
            ) : (
              <Field
                required={config.orderName.required && !order.orderName}
                label={tr(`CreateTimeslotSidebar.order`, `Order`)}
                errorMessage={findValidationMessage(validationErrors, `orders[${orderIndex}].orderName`, tr)}
              >
                <InputAndButtonGrid>
                  <div>
                    <RingilTextArea
                      rows={2}
                      value={order.orderName}
                      dataTestId={getOrderTestId(orderIndex, 'id')}
                      maxLength={orderNameLengthLimit}
                      onChange={v => {
                        onChange(
                          produce(orders, draft => {
                            draft[orderIndex].orderName = v.trim() ? v : null;
                          }),
                        );
                      }}
                      disabled={isReadOnly}
                    />
                  </div>
                </InputAndButtonGrid>
              </Field>
            )}
            {config.cargo?.visibility !== 'never' && (
              <>
                {findValidationMessageRaw(validationErrors, `orders[${orderIndex}].items`) && (
                  <COInlineMessage variant={'error'}>{findValidationMessageRaw(validationErrors, `orders[${orderIndex}].items`)?.message}</COInlineMessage>
                )}
                {order.items.map((item, itemIndex) => {
                  return (
                    <React.Fragment key={itemIndex}>
                      <div className={'grid grid-cols-[2fr,1fr] gap-2'}>
                        <div className={'flex-grow'}>
                          <Field
                            required={true}
                            label={tr(`CreateTimeslotSidebar.manipulationUnit`, `Manipulation unit SKU`)}
                            errorMessage={findValidationMessage(validationErrors, `orders[${orderIndex}].items[${itemIndex}].cargoTemplateCode`, tr)}
                            disabled={isReadOnly}
                          >
                            <InputSKU
                              fullWidth
                              value={item.cargoTemplateCode}
                              companyId={companyId}
                              onChange={v => {
                                onChange(
                                  produce(orders, draft => {
                                    if (isNotNullOrUndefined(draft[orderIndex].items)) {
                                      draft[orderIndex].items[itemIndex] = item;
                                    }
                                    draft[orderIndex].items[itemIndex].cargoTemplateCode = v;
                                  }),
                                );
                              }}
                              onChangeCargoTemplate={v => processChangeCargo(orderIndex, itemIndex, v)}
                              dataTestId={getManUnitBaseTestId(orderIndex, itemIndex)}
                              dataTestItemIdPrefix={getManUnitBaseTestId(orderIndex, itemIndex)}
                            />
                          </Field>
                        </div>
                        <div className={'flex-grow-0'}>
                          <Field
                            required={true}
                            label={tr(`CreateTimeslotSidebar.amount`, `Amount`)}
                            errorMessage={findValidationMessage(validationErrors, `orders[${orderIndex}].items[${itemIndex}].amount`, tr)}
                            disabled={isReadOnly}
                          >
                            <InputTextWithSuffix
                              suffix={tr('OrdersComponent.ks', 'ks')}
                              inputTextPosition={'right'}
                              type={item.cargoTemplateCode}
                              value={item.amount}
                              onChange={v => {
                                onChange(
                                  produce(orders, draft => {
                                    draft[orderIndex].items[itemIndex].amount = parseNumberOrUndefined(v);
                                  }),
                                );
                              }}
                              button={
                                !isReadOnly && (config.cargo.visibility !== 'always' || itemIndex > 0) ? (
                                  <RingilButton
                                    mode={'table'}
                                    size={'small'}
                                    icon={<SvgDelete />}
                                    tooltip={tr('OrdersComponent.deleteOrderItem', 'Delete this cargo item')}
                                    onClick={() => deleteOrderItem(orderIndex, itemIndex)}
                                  />
                                ) : null
                              }
                            >
                              <div data-testid={getManUnitTestId(orderIndex, itemIndex, 'Amount')} className="w-full">
                                <InputNumber
                                  value={item.amount}
                                  inputId={getManUnitTestId(orderIndex, itemIndex, 'Amount')}
                                  onChange={v => {
                                    onChange(
                                      produce(orders, draft => {
                                        draft[orderIndex].items[itemIndex].amount = v.value;
                                      }),
                                    );
                                  }}
                                  maxFractionDigits={item.decimalPlaces}
                                  className={'w-full outline-none no-spinner h-[42px]'}
                                  style={{ margin: 0 }}
                                  disabled={isReadOnly}
                                />
                              </div>
                            </InputTextWithSuffix>
                          </Field>
                        </div>
                      </div>
                      {config?.stackable?.visibility !== 'never' && (
                        <InputStackable
                          stackable={item.stackable}
                          onChange={v => {
                            onChange(
                              produce(orders, draft => {
                                draft[orderIndex].items[itemIndex].stackable = v;
                              }),
                            );
                          }}
                          disabled={isReadOnly}
                          dataTestId={getManUnitTestId(orderIndex, itemIndex, 'stackable')}
                        />
                      )}
                      {!isReadOnly &&
                        config?.itemNote?.visibility !== 'never' &&
                        wrapSecured(
                          fieldSecured?.('itemNote', orderIndex, itemIndex),
                          <InputNoteWithLink
                            note={item.itemNote}
                            onChanges={v => {
                              onChange(
                                produce(orders, draft => {
                                  draft[orderIndex].items[itemIndex].itemNote = v;
                                }),
                              );
                            }}
                            noteLabel={tr(`CreateTimeslotSidebar.orderItemNote`, `Order item note`)}
                            dataTestIdButton={getManUnitTestId(orderIndex, itemIndex, 'NoteManUnit')}
                            dataTestIdInput={getManUnitTestId(orderIndex, itemIndex, 'NoteWrite')}
                            maxLength={4000}
                          />,
                        )}

                      <VisibilityComponent
                        visibility={config.cargoDimensions.visibility}
                        switchComponent={<LinkWithIcon label={tr('OrdersComponent.rozmeryAHmotnost', 'Dimensions and weight')} icon={<SvgIconPlus />} />}
                        defaultOpen={
                          config.cargoDimensions.visibility === 'optional' && (item.width != null || item.height != null || item.length != null || item.weight != null)
                        }
                        dataTestId={getManUnitTestId(orderIndex, itemIndex, 'dimensions')}
                      >
                        <div className={'grid grid-cols-2 gap-2'}>
                          <Field
                            label={tr('OrdersComponent.delka(celkova)', 'Length (total)')}
                            required={config.cargoDimensions.items.length.required && item.cargoTemplateCode !== 'LOADING_METER'}
                            errorMessage={findValidationMessage(validationErrors, `orders[${orderIndex}].items[${itemIndex}].length`, tr)}
                            disabled={isReadOnly}
                          >
                            <InputTextWithSuffix
                              suffix={'cm'}
                              fullWidth
                              type={'number'}
                              value={item.length}
                              onChange={v => {
                                onChange(
                                  produce(orders, draft => {
                                    draft[orderIndex].items[itemIndex].length = parseNumberOrUndefined(v);
                                  }),
                                );
                              }}
                              dataTestId={getManUnitTestId(orderIndex, itemIndex, 'Length')}
                            />
                          </Field>
                          <Field
                            label={tr('OrdersComponent.sirkaJednotky', 'Width of unit')}
                            required={config.cargoDimensions.items.width.required && item.cargoTemplateCode !== 'LOADING_METER'}
                            errorMessage={findValidationMessage(validationErrors, `orders[${orderIndex}].items[${itemIndex}].width`, tr)}
                            disabled={isReadOnly}
                          >
                            <InputTextWithSuffix
                              suffix={'cm'}
                              fullWidth
                              type={'number'}
                              value={item.width}
                              onChange={v => {
                                onChange(
                                  produce(orders, draft => {
                                    draft[orderIndex].items[itemIndex].width = parseNumberOrUndefined(v);
                                  }),
                                );
                              }}
                              dataTestId={getManUnitTestId(orderIndex, itemIndex, 'Width')}
                            />
                          </Field>
                          <Field
                            label={tr('OrdersComponent.vyskaJednotky', 'Height of unit')}
                            required={config.cargoDimensions.items.height.required && item.cargoTemplateCode !== 'LOADING_METER'}
                            errorMessage={findValidationMessage(validationErrors, `orders[${orderIndex}].items[${itemIndex}].height`, tr)}
                            disabled={isReadOnly}
                          >
                            <InputTextWithSuffix
                              suffix={'cm'}
                              fullWidth
                              type={'number'}
                              value={item.height}
                              onChange={v => {
                                onChange(
                                  produce(orders, draft => {
                                    draft[orderIndex].items[itemIndex].height = parseNumberOrUndefined(v);
                                  }),
                                );
                              }}
                              dataTestId={getManUnitTestId(orderIndex, itemIndex, 'Height')}
                            />
                          </Field>
                          <Field
                            label={tr('OrdersComponent.hmotnost', 'Weight')}
                            required={config.cargoDimensions.items.weight.required}
                            errorMessage={findValidationMessage(validationErrors, `orders[${orderIndex}].items[${itemIndex}].weight`, tr)}
                            disabled={isReadOnly}
                          >
                            <InputTextWithSuffix
                              suffix={'kg'}
                              fullWidth
                              type={'number'}
                              value={item.weight}
                              onChange={v => {
                                onChange(
                                  produce(orders, draft => {
                                    draft[orderIndex].items[itemIndex].weight = parseNumberOrUndefined(v);
                                  }),
                                );
                              }}
                              dataTestId={getManUnitTestId(orderIndex, itemIndex, 'Weight')}
                            />
                          </Field>
                        </div>
                      </VisibilityComponent>
                      {item.details && item.details.length > 0 && (
                        <Field
                          label={tr(`RingilOrder.manipulationUnitInfoLabel`, `Manipulation units:`)}
                        >
                          <div className="flex flex-col">
                            {item.details
                              .map(mu =>
                                [
                                  isNotBlank(mu.externalId) ? mu.externalId : null,
                                  isNotBlank(mu.name) ? mu.name : null,
                                  mu.count != null ? `${mu.count}` : null,
                                  mu.weight != null && mu.weight > 0 ? formatWeight(mu.weight) : null,
                                  isNotBlank(mu.size) ? mu.size : null,
                                  isNotBlank(mu.wheelbase) ? mu.wheelbase : null,
                                ]
                                  .filter(Boolean)
                                  .join(', ')
                              )
                              .map(unit => <span>{unit}</span>)}
                          </div>
                        </Field>
                      )}
                      <HorizontalLine variant={'tertiary'} />
                    </React.Fragment>
                  );
                })}

                {!isReadOnly && (
                  <RingilButton
                    mode={'outlined-rectangle'}
                    size={'big'}
                    className={'self-start'}
                    icon={<SvgPlusCircle className={'size-4 shrink-0 overflow-visible'} />}
                    label={tr(`AddOrderItemButtonWrapper.orderItem`, `Order item`)}
                    onClick={() => addOrderItem(orderIndex)}
                    dataTestId={getOrderTestId(orderIndex, 'addCargoItem')}
                  />
                )}
              </>
            )}
            <VisibilityComponent
              visibility={config.cargoDetails?.visibility}
              switchComponent={<LinkWithIcon label={tr('OrdersComponent.informaceOZbozi', 'Information about goods')} icon={<SvgIconPlus />}></LinkWithIcon>}
              defaultOpen={
                config.cargoDetails?.visibility === 'optional' &&
                (order.orderAmount != null || order.orderDescription != null || order.orderValue?.price != null || order.orderValue?.currency != null)
              }
              onVisibilityChange={isOpen => setIsCargoDetailsOpen(isOpen)}
              dataTestId={getOrderTestId(orderIndex, 'goodsInfo')}
            >
              <>
                <div className={'grid grid-cols-2 gap-2'}>
                  {wrapSecured(
                    fieldSecured?.('orderAmount', orderIndex),
                    <Field
                      label={tr(`CreateTimeslotSidebar.productsInOrder`, `Products in order`)}
                      errorMessage={findValidationMessage(validationErrors, `orders[${orderIndex}].orderAmount`, tr)}
                      required={isCargoDetailsOpen && config.cargoDetails.items.orderAmount.required}
                      disabled={isReadOnly}
                    >
                      <InputTextWithSuffix
                        suffix={'ks'}
                        type={'number'}
                        value={order.orderAmount}
                        onChange={v => {
                          onChange(
                            produce(orders, draft => {
                              draft[orderIndex].orderAmount = parseNumberOrUndefined(v);
                            }),
                          );
                        }}
                        fullWidth
                        dataTestId={getOrderTestId(orderIndex, 'goodsInfo-numProducts')}
                      />
                    </Field>,
                  )}
                  <Field
                    label={tr(`CreateTimeslotSidebar.productDescription`, `Description`)}
                    errorMessage={findValidationMessage(validationErrors, `orders[${orderIndex}].orderDescription`, tr)}
                    required={isCargoDetailsOpen && config.cargoDetails.items.orderDescription.required}
                    disabled={isReadOnly}
                  >
                    <RingilTextArea
                      rows={1}
                      value={order.orderDescription ? order.orderDescription : ''}
                      onChange={value => {
                        onChange(
                          produce(orders, draft => {
                            draft[orderIndex].orderDescription = value || null;
                          }),
                        );
                      }}
                      maxLength={255}
                      className={'resize-none'}
                      dataTestId={getOrderTestId(orderIndex, 'goodsInfo-description')}
                    />
                  </Field>
                </div>
                {wrapSecured(
                  fieldSecured?.('orderValue', orderIndex),
                  <Field
                    label={tr('OrdersComponent.hodnotaZbozi', 'Value of goods')}
                    required={config.cargoDetails.items.orderValue.required}
                    errorMessage={findValidationMessage(validationErrors, `orders[${orderIndex}].orderValue`, tr)}
                    disabled={isReadOnly}
                  >
                    <InputPriceWithCurrency
                      value={order.orderValue?.price}
                      currency={order.orderValue?.currency}
                      changePrice={v => {
                        onChange(
                          produce(orders, draft => {
                            draft[orderIndex].orderValue = {
                              currency: draft[orderIndex].orderValue?.currency,
                              price: v,
                            };
                          }),
                        );
                      }}
                      changeCurrency={v => {
                        onChange(
                          produce(orders, draft => {
                            draft[orderIndex].orderValue = {
                              currency: v,
                              price: draft[orderIndex].orderValue?.price,
                            };
                          }),
                        );
                      }}
                      companyId={companyId}
                      dataTestId={getOrderTestId(orderIndex, 'goodsInfo-valueOfGoods')}
                      dataTestIdCurrency={getOrderTestId(orderIndex, 'goodsInfo-valueOfGoodsCurrency')}
                    />
                  </Field>,
                )}
              </>
            </VisibilityComponent>


            {config.orderTotalWeight.render === true && (
              <div className={volumeOfOrderEnabled ? 'grid grid-cols-2 gap-2' : undefined}>
                {order.items?.length > 0 ? (
                  <Field
                    readOnly={true}
                    label={tr(`CreateTimeslotSidebar.orderTotalWeight`, `Total weight`)}
                    errorMessage={findValidationMessage(validationErrors, `orderTotalWeight`, tr)}
                  >
                    <InputTextWithSuffix
                      suffix={'kg'}
                      readOnly={true}
                      value={order.items.reduce((acc, item) => acc + (item.weight || 0), 0)}
                      onChange={() => {}}
                      fullWidth
                      type={'number'}
                    />
                  </Field>
                ) : (
                  <Field
                    readOnly={false}
                    label={tr(`CreateTimeslotSidebar.orderTotalWeight`, `Total weight`)}
                    required={config.orderTotalWeight.required}
                    errorMessage={findValidationMessage(validationErrors, `orders[${orderIndex}].orderTotalWeight`, tr)}
                  >
                    <InputTextWithSuffix
                      suffix={'kg'}
                      readOnly={true}
                      value={order.orderTotalWeight}
                      onChange={v => {
                        const newValue = produce(orders, draft => {
                          draft[orderIndex].orderTotalWeight = parseNumberOrUndefined(v);
                        });

                        onChange(newValue);
                      }}
                      fullWidth
                      type={'number'}
                    />
                  </Field>
                )}
                {volumeOfOrderEnabled && (
                  <Field readOnly={true} label={tr(`CreateTimeslotSidebar.orderTotalVolume`, `Total volume`)}>
                    <InputTextWithSuffix
                      suffix={'m³'}
                      readOnly={true}
                      value={Number(calculateOrderVolume(order.items).toFixed(2))}
                      onChange={() => {}}
                      fullWidth
                      type={'number'}
                    />
                  </Field>
                )}
              </div>
            )}
            {!isReadOnly && (
              <InputNoteWithLink
                note={order.orderNote}
                onChanges={v => {
                  onChange(
                    produce(orders, draft => {
                      draft[orderIndex].orderNote = v;
                    }),
                  );
                }}
                noteLabel={tr(`CreateTimeslotSidebar.orderNote`, `Order note`)}
                dataTestIdButton={getOrderTestId(orderIndex, 'noteToOrder')}
                dataTestIdInput={getOrderTestId(orderIndex, 'noteToOrder-input')}
                maxLength={4000}
              />
            )}
            <FoldableEanCodes eanCodes={order.eanCodes} />

            {config.unloadingLocationId.render === true && (
              <>
                <COHeadline title={tr('OrdersComponent.vylozitObjednavku', 'Unload order')} variant={'h4'} icon={<SvgLoadDown />} />
                <Field
                  required={config.unloadingLocationId.required}
                  errorMessage={findValidationMessage(validationErrors, `orders[${orderIndex}].unloadingLocationId`, tr)}
                >
                  <Dropdown
                    value={order.unloadingLocationId}
                    options={allUnloadingLocations}
                    optionLabel={'name'}
                    optionValue={'id'}
                    onChange={e => {
                      onChange(
                        produce(orders, draft => {
                          draft[orderIndex].unloadingLocationId = e.value;
                        }),
                      );
                    }}
                    disabled={isReadOnly}
                    placeholder="Vyberte místo vykládky"
                  />
                </Field>
              </>
            )}
          </OrderWrapper>
        );
      })}
      <div className={`flex flex-row flex-wrap items-center gap-4 ${orders?.length >= 2 ? 'mt-4' : ''}`}>
        {!isReadOnly && (
          <RingilButton
            mode={'outlined-rectangle'}
            size={'big'}
            icon={<SvgPlusCircle className={'size-4 shrink-0 overflow-visible'} />}
            label={tr(`AddOrderButton.order`, `Order`)}
            onClick={() => addOrder()}
            dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-addOrder` : undefined}
          />
        )}
        {footerActions}
      </div>
    </ColEven>
  );
};

export default OrdersComponent;
